// Sincronizador (documento 06, F10; documento 07). Reglas:
// 1. Las operaciones salen en el orden en que se crearon, en lotes de hasta 50, por POST /sync/operaciones.
// 2. Antes de enviar una entrega se suben sus fotos y firmas; si no se puede, la entrega sale igual y el archivo se
//    reintenta después asociado a la parada.
// 3. Una rechazada no frena a las demás, salvo `confirmar_carga` o `salir` (todo lo siguiente depende de ellas):
//    la cola se detiene hasta que el chofer la descarte.
// 4. Si la carga ya la confirmó otro dispositivo (RUTA_EXISTENTE), se descartan las operaciones que dependían de la
//    ruta local y se recarga la del servidor.
// 5. Después de cada lote con algo aplicado, se vuelve a pedir el paquete: el servidor manda.
import type { ComprobanteImprimible, ResultadoOperacion } from '@reparto/shared/nucleo';
import { create } from 'zustand';
import { api, enLinea, ErrorApi, SinRed } from './api';
import { subirArchivo, subirRezagados } from './archivos';
import { db, type OperacionLocal } from './db';
import { refrescarPaquete } from './paquete';
import { guardarDelServidor } from './facturacion';

export type EstadoSincronizacion = 'ok' | 'pendiente' | 'error' | 'sin_red' | 'enviando';

interface EstadoSync {
  estado: EstadoSincronizacion;
  ultimaSync: string | null;
  ultimoError: string | null;
  /** Aviso para mostrar una vez (por ejemplo, "la carga ya la confirmó otro dispositivo"). */
  aviso: string | null;
  fijar: (p: Partial<Omit<EstadoSync, 'fijar'>>) => void;
}

export const useSync = create<EstadoSync>((set) => ({
  estado: 'ok',
  ultimaSync: null,
  ultimoError: null,
  aviso: null,
  fijar: (p) => set(p),
}));

const BLOQUEANTES = new Set(['confirmar_carga', 'salir']);
const LOTE = 50;

let corriendo = false;
let otraVuelta = false;
let temporizador: ReturnType<typeof setTimeout> | null = null;

/** Pide un ciclo de sincronización pronto (se agrupan varias llamadas seguidas). */
export function despertar(demoraMs = 300): void {
  if (temporizador) clearTimeout(temporizador);
  temporizador = setTimeout(() => {
    temporizador = null;
    void sincronizar();
  }, demoraMs);
}

/** Operaciones que dependen de una ruta o parada (para descartarlas en cadena). */
function dependeDe(op: OperacionLocal, rutaId: string | undefined, paradaIds: Set<string>): boolean {
  const p = op.payload as { ruta_id?: string; parada_id?: string };
  return (!!rutaId && p.ruta_id === rutaId) || (!!p.parada_id && paradaIds.has(p.parada_id));
}

/** Descarta una operación y todo lo que dependía de ella. */
export async function descartar(operacionId: string, motivo = 'Descartada por el chofer'): Promise<number> {
  const op = await db.cola.where('operacion_id').equals(operacionId).first();
  if (!op) return 0;
  const payload = op.payload as { ruta_id?: string; paradas?: Array<{ parada_id: string }> };
  const rutaId = op.tipo === 'confirmar_carga' ? payload.ruta_id : undefined;
  const paradas = new Set(op.tipo === 'confirmar_carga' ? (payload.paradas ?? []).map((p) => p.parada_id) : []);
  let n = 0;
  await db.transaction('rw', db.cola, async () => {
    await db.cola.update(op.orden!, { estado: 'descartada', detalle: op.detalle ?? motivo });
    n++;
    if (rutaId) {
      const siguientes = await db.cola.where('estado').anyOf('pendiente', 'rechazada').toArray();
      for (const s of siguientes) {
        if ((s.orden ?? 0) > (op.orden ?? 0) && dependeDe(s, rutaId, paradas)) {
          await db.cola.update(s.orden!, { estado: 'descartada', detalle: 'Dependía de una operación descartada' });
          n++;
        }
      }
    }
  });
  despertar(0);
  return n;
}

/** Vuelve a intentar una operación rechazada (por ejemplo, después de corregir la configuración). */
export async function reintentar(operacionId: string): Promise<void> {
  const op = await db.cola.where('operacion_id').equals(operacionId).first();
  if (op?.estado === 'rechazada')
    await db.cola.update(op.orden!, { estado: 'pendiente', codigo: undefined, detalle: undefined });
  despertar(0);
}

async function bloqueo(): Promise<OperacionLocal | undefined> {
  const rechazadas = await db.cola.where('estado').equals('rechazada').toArray();
  return rechazadas.find((o) => BLOQUEANTES.has(o.tipo));
}

/** sinConexion: el último ciclo no llegó al servidor aunque el sistema diga que hay red (señal débil, API caída). */
async function actualizarEstado(sinConexion = false): Promise<void> {
  const [pend, rech] = await Promise.all([
    db.cola.where('estado').anyOf('pendiente', 'enviando').count(),
    db.cola.where('estado').equals('rechazada').count(),
  ]);
  const fijar = useSync.getState().fijar;
  if (sinConexion || !enLinea()) fijar({ estado: 'sin_red' });
  else if (rech) fijar({ estado: 'error' });
  else if (pend) fijar({ estado: 'pendiente' });
  else fijar({ estado: 'ok' });
}

/** Un ciclo completo. Devuelve cuántas operaciones se aplicaron. */
export async function sincronizar(): Promise<number> {
  if (corriendo) {
    otraVuelta = true;
    return 0;
  }
  corriendo = true;
  let aplicadas = 0;
  let sinConexion = false;
  const fijar = useSync.getState().fijar;
  try {
    const sesion = await db.sesion.get('actual');
    if (!sesion?.access_token) return 0;
    for (;;) {
      if (await bloqueo()) break;
      const lote = await db.cola
        .where('estado')
        .equals('pendiente')
        .sortBy('orden')
        .then((l) => l.slice(0, LOTE));
      if (!lote.length) break;
      fijar({ estado: 'enviando' });

      // Fotos y firmas primero.
      for (const op of lote) {
        if (!op.archivos_locales?.length) continue;
        const ids: string[] = [];
        for (const local of op.archivos_locales) {
          try {
            ids.push(await subirArchivo(local));
          } catch (e) {
            if (e instanceof SinRed) throw e;
            // Error del servidor o del almacenamiento: la entrega sale sin el archivo y se reintenta después.
            await db.archivos.update(local, { estado: 'fallido' });
          }
        }
        op.payload = { ...op.payload, archivo_ids: ids };
        await db.cola.update(op.orden!, { payload: op.payload });
      }

      await db.cola.bulkUpdate(
        lote.map((o) => ({ key: o.orden!, changes: { estado: 'enviando', intentos: o.intentos + 1 } })),
      );
      let respuesta: { resultados: ResultadoOperacion[] };
      try {
        respuesta = await api<{ resultados: ResultadoOperacion[] }>('/sync/operaciones', {
          cuerpo: {
            dispositivo_id: sesion.dispositivo_id,
            operaciones: lote.map((o) => ({
              operacion_id: o.operacion_id,
              tipo: o.tipo,
              payload: o.payload,
              hora_dispositivo: o.hora_dispositivo,
            })),
          },
          timeoutMs: 30_000,
        });
      } catch (e) {
        await db.cola.bulkUpdate(lote.map((o) => ({ key: o.orden!, changes: { estado: 'pendiente' } })));
        throw e;
      }

      const porId = new Map(respuesta.resultados.map((r) => [r.operacion_id, r]));
      const ahora = new Date().toISOString();
      let conflictoCarga: OperacionLocal | null = null;
      let conformidadRechazada: { op: OperacionLocal; detalle: string } | null = null;
      for (const op of lote) {
        const r = porId.get(op.operacion_id);
        if (!r) {
          await db.cola.update(op.orden!, { estado: 'pendiente' });
          continue;
        }
        if (r.resultado === 'aplicada' || r.resultado === 'duplicada') {
          await db.cola.update(op.orden!, {
            estado: 'aplicada',
            respuesta: r.estado,
            aplicada_en: ahora,
            codigo: undefined,
            detalle: undefined,
          });
          aplicadas++;
          // El documento definitivo de la entrega (decisión D57): reemplaza al que armó el celular (el provisorio o el
          // de la reserva, si el servidor tuvo que cambiarle el número).
          const doc = (
            r.estado as { comprobante?: (ComprobanteImprimible & { pedido_traslado_id?: string }) | null } | null
          )?.comprobante;
          if (op.tipo === 'entregar' && doc?.pedido_traslado_id)
            await guardarDelServidor(doc.pedido_traslado_id, doc).catch(() => undefined);
        } else {
          await db.cola.update(op.orden!, { estado: 'rechazada', codigo: r.codigo, detalle: r.detalle });
          if (op.tipo === 'confirmar_carga' && r.codigo === 'RUTA_EXISTENTE') conflictoCarga = op;
          // El "Recibí conforme" (sin ruta propuesta: la cierra el servidor, decisión D54) no deja nada armado en el
          // celular: si se rechaza (la carga cambió, falta un motivo…), se descarta, se avisa y se vuelve a mirar.
          else if (op.tipo === 'confirmar_carga' && !(op.payload as { ruta_id?: string }).ruta_id)
            conformidadRechazada = { op, detalle: r.detalle ?? 'No se pudo tomar' };
        }
      }
      if (conflictoCarga) {
        await descartar(conflictoCarga.operacion_id, 'La carga ya la confirmó otro dispositivo');
        fijar({ aviso: 'La carga de este móvil ya la confirmó otro dispositivo. Se cargó la ruta del servidor.' });
      }
      if (conformidadRechazada) {
        await descartar(conformidadRechazada.op.operacion_id, conformidadRechazada.detalle);
        fijar({
          aviso: `Tu "Recibí conforme" no se tomó: ${conformidadRechazada.detalle}. Revisá la carga y confirmá de nuevo.`,
        });
      }
      if (aplicadas || conflictoCarga || conformidadRechazada) {
        try {
          await refrescarPaquete();
        } catch (e) {
          if (e instanceof SinRed) throw e;
        }
      }
      if (lote.length < LOTE) break;
    }
    await subirRezagados().catch(() => 0);
    fijar({ ultimaSync: new Date().toISOString(), ultimoError: null });
  } catch (e) {
    if (e instanceof SinRed) sinConexion = true;
    else if (e instanceof ErrorApi && e.estado === 401)
      fijar({ estado: 'error', ultimoError: 'La sesión venció. Ingresá de nuevo para enviar lo pendiente.' });
    else fijar({ estado: 'error', ultimoError: e instanceof Error ? e.message : String(e) });
  } finally {
    corriendo = false;
    await actualizarEstado(sinConexion);
    if (otraVuelta) {
      otraVuelta = false;
      despertar(0);
    }
  }
  return aplicadas;
}

/** Arranca el ciclo automático: al volver la red, al mostrarse la app y cada 30 s mientras está visible. */
export function iniciarSincronizador(): () => void {
  const alVolver = () => despertar(0);
  window.addEventListener('online', alVolver);
  window.addEventListener('offline', () => useSync.getState().fijar({ estado: 'sin_red' }));
  document.addEventListener('visibilitychange', () => document.visibilityState === 'visible' && despertar(0));
  const intervalo = setInterval(() => document.visibilityState === 'visible' && despertar(0), 30_000);
  despertar(0);
  return () => {
    window.removeEventListener('online', alVolver);
    clearInterval(intervalo);
  };
}
